import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse, ApiMethods, LoginCredentials, RegisterData, ProfileFormData, UserFilters, UserListResponse, AdminStats, ParticipantFilters, ParticipantsResponseData, User } from '../types';
import apiCache from './apiCache';
import { retryRequest } from './retryHandler';

// API Configuration - Using production URLs (Heroku)
export const API_CONFIG = {
  B2B_API_URL: process.env.REACT_APP_B2B_API_URL || "https://globalchic-b2b-auth-ff78d2b7565c.herokuapp.com",
  EVENTS_API_URL: process.env.REACT_APP_EVENTS_API_URL || "https://globalchic-b2b-auth-ff78d2b7565c.herokuapp.com", // Temporary - will be separate service later
  NOTIFICATION_API_URL: process.env.REACT_APP_NOTIFICATION_API_URL || "https://globalchic-b2b-auth-ff78d2b7565c.herokuapp.com", // Temporary - will be separate service later
  VENDOR_API_URL: process.env.REACT_APP_VENDOR_API_URL || "https://globalchic-b2b-auth-ff78d2b7565c.herokuapp.com", // Temporary - will be separate service later
};

// API Endpoints
export const API_ENDPOINTS = {
  // B2B Authentication
  B2B_LOGIN: `${API_CONFIG.B2B_API_URL}/api/b2b/auth/login`,
  B2B_REGISTER: `${API_CONFIG.B2B_API_URL}/api/b2b/auth/register`,
  B2B_LOGOUT: `${API_CONFIG.B2B_API_URL}/api/b2b/auth/logout`,
  B2B_FORGOT_PASSWORD: `${API_CONFIG.B2B_API_URL}/api/b2b/auth/forgot-password`,
  B2B_RESET_PASSWORD: `${API_CONFIG.B2B_API_URL}/api/b2b/auth/reset-password`,
  B2B_VERIFY_EMAIL: `${API_CONFIG.B2B_API_URL}/api/b2b/auth/verify-email`,
  B2B_RESEND_VERIFICATION: `${API_CONFIG.B2B_API_URL}/api/b2b/auth/resend-verification`,

  // B2B User Management
  B2B_USER_PROFILE: `${API_CONFIG.B2B_API_URL}/api/b2b/users/profile`,
  B2B_UPDATE_PROFILE: `${API_CONFIG.B2B_API_URL}/api/b2b/users/profile`,
  B2B_DELETE_PROFILE: `${API_CONFIG.B2B_API_URL}/api/b2b/users/profile`,
  B2B_UPDATE_LAST_SEEN: `${API_CONFIG.B2B_API_URL}/api/b2b/users/last-seen`,
  B2B_PROFILE_COMPLETION: `${API_CONFIG.B2B_API_URL}/api/b2b/users/profile-completion`,
  B2B_ADMIN_USER_PROFILE: `${API_CONFIG.B2B_API_URL}/api/b2b/admin/user-profile`,
  B2B_ADMIN_UPDATE_NOTES: `${API_CONFIG.B2B_API_URL}/api/b2b/admin/update-notes`,

  // B2B Participants
  B2B_PARTICIPANTS: `${API_CONFIG.B2B_API_URL}/api/b2b/users/participants`,

  // B2B Admin
  B2B_ADMIN_USERS: `${API_CONFIG.B2B_API_URL}/api/b2b/admin/users`,
  B2B_ADMIN_STATS: `${API_CONFIG.B2B_API_URL}/api/b2b/admin/stats`,
  B2B_ADMIN_RESEND_APPROVAL: `${API_CONFIG.B2B_API_URL}/api/b2b/admin/resend-approval-email`,
  B2B_PUBLIC_RESEND_APPROVAL: `${API_CONFIG.B2B_API_URL}/api/b2b/public/resend-approval-email`,

  // Events (temporary routing through B2B service)
  EVENTS_LIST: `${API_CONFIG.EVENTS_API_URL}/api/events`,
  EVENT_DETAILS: `${API_CONFIG.EVENTS_API_URL}/api/events`,

  // Notifications (temporary routing through B2B service)
  NOTIFICATIONS: `${API_CONFIG.NOTIFICATION_API_URL}/api/notifications`,

  // Vendor Management (temporary routing through B2B service)
  VENDORS: `${API_CONFIG.VENDOR_API_URL}/api/vendors`,
};

// Default headers function
const getDefaultHeaders = (includeAuth: boolean = true): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (includeAuth) {
    const token = localStorage.getItem('b2bToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return headers;
};

// Generic API request function with proper typing and caching
const apiRequest = async <T = any>(
  url: string,
  options: RequestInit = {},
  bypassCache: boolean = false
): Promise<ApiResponse<T>> => {
  const method = options.method || 'GET';
  const cacheKey = apiCache.generateKey(url, options);
  
  // For GET requests, use caching and deduplication (unless bypassed)
  if (method === 'GET' && !bypassCache) {
    return await apiCache.getOrCreate(cacheKey, async () => {
      return await makeActualRequest<T>(url, options);
    });
  }
  
  // For authentication requests (login/register), bypass cache entirely for now
  if (url.includes('/auth/login') || url.includes('/auth/register')) {
    console.log("🔓 Bypassing cache for auth request:", url);
    return await makeActualRequest<T>(url, options);
  }
  
  // For other non-GET requests or when cache is bypassed, make the request directly
  if (bypassCache) {
    console.log("🔄 Bypassing cache for request:", url);
  }
  return await makeActualRequest<T>(url, options);
};

// Actual request function without caching
const makeActualRequest = async <T = any>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  // Determine request type for retry strategy
  const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
  const requestType = isAuthEndpoint ? 'auth' : 'general';
  
  return await retryRequest(async () => {
    try {
      const defaultOptions: RequestInit = {
        method: 'GET',
        // Don't attach Bearer token to login/register calls
        headers: getDefaultHeaders(!isAuthEndpoint),
      };

      const config = { ...defaultOptions, ...options };
      
      // Merge headers properly
      config.headers = {
        ...defaultOptions.headers,
        ...options.headers,
      };

      // Debug headers for admin requests
      if (url.includes('/admin/')) {
        console.log('Admin API request headers:', config.headers);
        if (config.headers && typeof config.headers === 'object' && 'Authorization' in config.headers) {
          console.log('Authorization header:', (config.headers as any).Authorization);
        }
      }

      const response = await fetch(url, config);
      
      // Handle HTTP errors
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle token expiration (401 Unauthorized)
        if (response.status === 401) {
          // IMPORTANT:
          // - For login failures (wrong / non-existent email), do NOT redirect. Let the form show the error.
          // - For protected requests, clear auth data and redirect to canonical /gcb/login.
          if (!isAuthEndpoint) {
            console.log('Token expired - clearing auth data and redirecting to login');
            localStorage.removeItem('b2bToken');
            localStorage.removeItem('b2bUser');

            const path = window.location.pathname || "";
            const alreadyOnLogin = path.startsWith('/gcb/login') || path.startsWith('/b2b/login');
            if (!alreadyOnLogin) {
              window.location.href = '/gcb/login';
            }
          }
        }
        
        // Handle pending approval (403 with PENDING_APPROVAL code) - this is a special case
        // The backend returns token and user data even though status is 403
        if (response.status === 403 && errorData.code === 'PENDING_APPROVAL') {
          // Return the error data as a response so the frontend can handle it
          // The error data contains token and user, which is what we need
          return {
            success: false,
            error: errorData.error || 'Account pending approval',
            code: errorData.code,
            data: {
              token: errorData.token,
              user: errorData.user,
            },
          } as any;
        }
        
        // Handle email not verified (403 with EMAIL_NOT_VERIFIED code)
        if (response.status === 403 && errorData.code === 'EMAIL_NOT_VERIFIED') {
          // Return the error data so the frontend can handle it
          return {
            success: false,
            error: errorData.error || 'Email not verified',
            code: errorData.code,
          } as any;
        }
        
        const error = new Error(
          errorData.message || errorData.error || `HTTP error! status: ${response.status}`
        );
        (error as any).response = { data: errorData, status: response.status };
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      
      // Handle token expiration from error message (fallback)
      const errorMessage = (error as any)?.message || '';
      if (errorMessage.includes('Token expired') || errorMessage.includes('401')) {
        if (!isAuthEndpoint) {
          console.log('Token expired detected in error message - clearing auth data and redirecting to login');
          localStorage.removeItem('b2bToken');
          localStorage.removeItem('b2bUser');
          // Redirect to login page
          const path = window.location.pathname || "";
          const alreadyOnLogin = path.startsWith('/gcb/login') || path.startsWith('/b2b/login');
          if (!alreadyOnLogin) {
            window.location.href = '/gcb/login';
          }
        }
      }
      
      throw error;
    }
  }, 3, 1000, requestType);
};

// Create axios instance for multipart form data
const axiosInstance: AxiosInstance = axios.create({
  timeout: 10000,
});

// Add request interceptor for auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('b2bToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('b2bToken');
      localStorage.removeItem('b2bUser');
      window.location.href = '/gcb/login';
    }
    return Promise.reject(error);
  }
);

// API methods with proper typing
export const api: ApiMethods = {
  // Authentication
  login: (credentials: LoginCredentials) =>
    apiRequest<{ user: User; token: string }>(API_ENDPOINTS.B2B_LOGIN, {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  logout: () =>
    apiRequest(API_ENDPOINTS.B2B_LOGOUT, {
      method: 'POST',
    }),

  refreshToken: () =>
    apiRequest<{ token: string }>('/api/auth/refresh', {
      method: 'POST',
    }),

  // Profile Management
  // Bypass cache: response is per-user by token; caching by URL would serve wrong user's profile
  getProfile: () =>
    apiRequest<User>(API_ENDPOINTS.B2B_USER_PROFILE, { method: "GET" }, true),

  createProfile: (profileData: Partial<ProfileFormData>) =>
    apiRequest<User>(API_ENDPOINTS.B2B_USER_PROFILE, {
      method: 'POST',
      body: JSON.stringify(profileData),
    }),

  updateUserProfile: async (profileData: Partial<ProfileFormData>) => {
    const result = await apiRequest<User>(API_ENDPOINTS.B2B_UPDATE_PROFILE, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
    
    // Clear profile-related cache after update
    apiCache.clearCache('/api/b2b/users/profile');
    apiCache.clearCache('/api/b2b/admin/user-profile');
    
    return result;
  },

  uploadProfilePhoto: async (file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await axiosInstance.post<ApiResponse<{ imageUrl: string }>>(
      `${API_CONFIG.B2B_API_URL}/api/b2b/images/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    );
    return response as any; // Type assertion to match expected return type
  },

  deleteProfile: async () => {
    const result = await apiRequest(API_ENDPOINTS.B2B_DELETE_PROFILE, {
      method: 'DELETE',
    });
    
    // Clear all cache after profile deletion
    apiCache.clearAllCache();
    
    return result;
  },

  // Admin Operations
  getUsers: (filters?: UserFilters) =>
    apiRequest<UserListResponse>(API_ENDPOINTS.B2B_ADMIN_USERS, {
      method: 'GET',
    }),

  // Note: User approval/rejection is now only available via email links
  // See public routes in backend for email-based approval endpoints

  deleteUser: (userId: string) =>
    apiRequest(`${API_ENDPOINTS.B2B_ADMIN_USERS}/${userId}`, {
      method: 'DELETE',
    }),

  getAdminStats: () =>
    apiRequest<AdminStats>(API_ENDPOINTS.B2B_ADMIN_STATS),

  // Participants
  getParticipants: (filters?: ParticipantFilters) => {
    const queryParams = new URLSearchParams();
    let hasFilters = false;
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            // Only append array if it has items
            if (value.length > 0) {
              queryParams.append(key, value.join(','));
              hasFilters = true;
            }
          } else {
            queryParams.append(key, value.toString());
            hasFilters = true;
          }
        }
      });
    }
    console.log("🔍 API getParticipants - Query params:", queryParams.toString());
    console.log("🔍 Has filters:", hasFilters);
    
    const url = queryParams.toString() 
      ? `${API_ENDPOINTS.B2B_PARTICIPANTS}?${queryParams.toString()}`
      : API_ENDPOINTS.B2B_PARTICIPANTS;
    
    // For filtered requests, bypass cache to ensure we get fresh data from backend
    // This is important because the backend filter might have been fixed but cache has stale data
    const shouldBypassCache = hasFilters;
    
    if (shouldBypassCache) {
      console.log("🔄 Bypassing cache for filtered participants request");
      // Clear all participants cache entries to ensure fresh data
      apiCache.clearCache('/api/b2b/users/participants');
    }
      
    return apiRequest<ParticipantsResponseData>(
      url,
      { method: 'GET' },
      shouldBypassCache
    );
  },

  // Profile completion check
  checkProfileCompletion: () =>
    apiRequest<{success: boolean, data: {isComplete: boolean, missingFields?: string[], details?: any}}>(API_ENDPOINTS.B2B_PROFILE_COMPLETION),

  // Admin profile management
  getUserProfileById: (userId: string) =>
    apiRequest<any>(`${API_ENDPOINTS.B2B_ADMIN_USER_PROFILE}/${userId}`, {
      method: 'GET',
    }),

  updateUserAdminNotes: (userId: string, adminNotes: string) =>
    apiRequest(`${API_ENDPOINTS.B2B_ADMIN_UPDATE_NOTES}/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ adminNotes }),
    }),

  // Public endpoint to resend approval email by user email
  resendApprovalEmailByEmail: (email: string) =>
    apiRequest<{success: boolean, message: string, data?: any}>(API_ENDPOINTS.B2B_PUBLIC_RESEND_APPROVAL, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }, true), // Bypass cache for this request
};

// Registration function (separate from main api object for legacy compatibility)
export const register = (data: RegisterData): Promise<ApiResponse<{ user: User; token: string }>> =>
  apiRequest<{ user: User; token: string }>(API_ENDPOINTS.B2B_REGISTER, {
    method: 'POST',
    body: JSON.stringify(data),
  });

// Legacy default export for backward compatibility
const apiConfig = {
  ...api,
  register,
};

export default apiConfig;

// Export types for use in components
export type { ApiResponse, ApiMethods };